import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useAllUserMonarxDetails } from '@newfold/huapi-js';

import NoHostingPackageCard from '~/apps/Websites/components/HostingSection/components/NoHostingPackageCard';
import { useMFEContext } from '~/components/MFEProvider';
import { useTenant } from '~/components/TenantProvider';
import {
  HOSTING_FETCH_LIMIT,
  HOSTING_LIST_PAGE_SS_KEY,
} from '~/components/TenantProvider/utils/constants';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import ProServicesCard from '~/scenes/Hosting/components/ProServicesCard';

import HowtoCard from '../Site/components/HowtoCard';
import LoadMoreButton from '../Site/components/LoadMoreButton';
import ErrorComponent from './components/ErrorComponent';
import MultiHostingOnBoarding from './components/MultiHostingOnBoarding';
import PackageUsageCard from './scenes/PackageUsage';
import {
  getPlanStatus,
  groupAllMonarxAddonsByHostingId,
} from './utils/helpers';

const HostingList = () => {
  const { t } = useTranslation('hosting');
  const mfeContext = useMFEContext() as { data?: { proServices?: any } };
  const proServicesData = mfeContext?.data?.proServices ?? null;
  const { brandFromJWT, howToHostingArticleUrl, marketPlaceUrl } =
    useBrandInfo();
  const isHostgator = brandFromJWT?.toLowerCase() === 'hostgator';

  const {
    hostingPackages: allHostingPlans = [],
    isHostingPackagesLoading: isFetching = false,
    isHostingPackagesError: isError = false,
    fetchHostingPackages = () => {},
    resetHostingPackages = () => {},
    isHostingPackagesEmpty = true,
    page: currentPage,
    showLoadMoreButton = false,
  } = useTenant();
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);

  const { data: monarxAddonsData } = useAllUserMonarxDetails();
  const monarxList = monarxAddonsData?.data?.rows || [];
  const monarxAddonGroupedList = groupAllMonarxAddonsByHostingId(monarxList);

  const plans = allHostingPlans;

  useEffect(() => {
    if (!isFetching && (plans.length > 0 || isHostingPackagesEmpty)) {
      setHasInitialLoad(true);
    }
  }, [isFetching, plans.length, isHostingPackagesEmpty]);

  useEffect(() => {
    if (isLoadMoreLoading && !isFetching) {
      setIsLoadMoreLoading(false);
    }
  }, [isLoadMoreLoading, isFetching]);

  const latestPlanId = useMemo(() => {
    if (!plans?.length) return null;
    // Find the latest plan based on date_added
    const latestHostingPlan = plans.reduce((latest, plan) => {
      const { date_added: dateAdded } = plan?.account_info || {};
      const { date_added: latestDateAdded } = latest?.account_info || {};
      const latestDate = latestDateAdded
        ? new Date(latestDateAdded).getTime()
        : 0;
      const planDate = dateAdded ? new Date(dateAdded).getTime() : 0;
      return planDate > latestDate ? plan : latest;
    }, plans[0]);

    return latestHostingPlan?.account_info?.id || null;
  }, [plans]);

  const handleLoadMore = () => {
    if (isLoadMoreLoading || isFetching) return;
    setIsLoadMoreLoading(true);

    const sessionPage = Number(
      sessionStorage.getItem(HOSTING_LIST_PAGE_SS_KEY),
    );
    const nextPage = sessionPage ? sessionPage + 1 : (currentPage ?? 1) + 1;
    fetchHostingPackages(nextPage, HOSTING_FETCH_LIMIT);
  };

  const handleRefetchOnError = () => {
    resetHostingPackages();
  };

  const handleAddHostingNavigate = () => {
    window.location.assign(marketPlaceUrl!);
  };

  const showLoadingSkeleton =
    (!hasInitialLoad || isFetching) && plans.length === 0;

  const isProServiceCardManageble =
    plans.length > 0 &&
    plans.every(
      (plan) => plan?.account_info?.addon_status?.toLowerCase() !== 'active',
    );

  if (hasInitialLoad && !isFetching && plans.length === 0) {
    return (
      <Stack spacing={4}>
        <Typography variant="h1">{t('hostingTitle')}</Typography>
        <NoHostingPackageCard showHowToCard={true} />
      </Stack>
    );
  }

  if (showLoadingSkeleton) {
    return (
      <Container sx={{ mt: 4 }} data-testid="hosting-list-skeleton">
        <Stack spacing={2}>
          <Skeleton variant="text" width={200} height={48} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" width="100%" height={200} />
          ))}
        </Stack>
      </Container>
    );
  }

  if (isError && !isFetching) {
    return (
      <>
        <Typography variant="h1">{t('hostingTitle')}</Typography>
        <ErrorComponent
          onRetry={handleRefetchOnError}
          errorMessage={t('hostingNova.failedToLoad')}
        />
      </>
    );
  }

  return (
    <>
      <Stack spacing={4}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography
            variant="h2"
            id="hosting-title"
            sx={{ fontSize: '26px !important' }}
          >
            {t('hostingTitle')}
          </Typography>
          <Button
            variant="contained"
            sx={{ mb: 3, justifyContent: 'flex-end' }}
            onClick={handleAddHostingNavigate}
            data-testid="add-hosting-plan-btn"
          >
            {t('hostingNova.addHostingPlanBtn')}
          </Button>
        </Stack>
        <Stack spacing={3}>
          {plans.map((plan) => (
            <PackageUsageCard
              key={plan?.account_info?.id}
              hostingPackDetails={plan}
              monarxAddonGroupedList={monarxAddonGroupedList}
            />
          ))}
        </Stack>

        {plans.length > 0 && proServicesData?.link && (
          <Grid item xs={12}>
            <ProServicesCard
              proServicesData={proServicesData}
              isManagable={isProServiceCardManageble}
              hostingStatus={getPlanStatus(plans)}
            />
          </Grid>
        )}

        {plans.length > 0 && showLoadMoreButton && (
          <Grid container justifyContent="center" sx={{ mt: 2 }}>
            <Grid item>
              <LoadMoreButton
                loading={isLoadMoreLoading || isFetching}
                page={currentPage ?? 1}
                onNext={handleLoadMore}
                label={t('hostingNova.loadMore')}
                data-testid="load-more-btn"
              />
            </Grid>
          </Grid>
        )}
      </Stack>

      <HowtoCard
        title={isHostgator ? 'hosting.hostgator.title' : 'hosting.title'}
        description={
          isHostgator ? 'hosting.hostgator.description' : 'hosting.description'
        }
        displayVideoArticleCard={false}
        articleUrl={howToHostingArticleUrl}
      />

      {latestPlanId && (
        <MultiHostingOnBoarding
          latestPlanHostingId={latestPlanId}
          totalProductCount={plans.length}
        />
      )}
    </>
  );
};

export default HostingList;
